const RICH_CELL_SELECTOR = ".video-mention, .mention";

export type ParsedTableCell =
	| { kind: "text"; value: string }
	| { kind: "rich"; nodes: Node[] };

export function parseTableCell(cell: HTMLTableCellElement): ParsedTableCell {
	if (cell.querySelector(RICH_CELL_SELECTOR)) {
		return { kind: "rich", nodes: Array.from(cell.childNodes) };
	}
	return { kind: "text", value: cell.textContent ?? "" };
}

/**
 * The header row and body rows of a rendered markdown <table>.
 *
 * Markdown tables from {@link formatMarkdown} have no `<thead>`; browsers normalize
 * `<tr>` children into `<tbody>`, so body rows must be taken via `table.rows` with the
 * header row count skipped — not via `tbody tr` alone.
 */
export function tableRowElements(table: HTMLTableElement): {
	header: HTMLTableRowElement | undefined;
	body: HTMLTableRowElement[];
} {
	const headerRowCount =
		table.tHead?.rows.length ?? (table.rows.length > 0 ? 1 : 0);
	const header =
		table.tHead && table.tHead.rows.length > 0
			? table.tHead.rows[0]
			: table.rows[0];
	return {
		header,
		body: Array.from(table.rows).slice(headerRowCount),
	};
}

/**
 * Parses a rendered markdown <table> into headers and body rows.
 * Shared by chat streaming and static markdown surfaces (e.g. answer panel).
 *
 * Cells that contain hydrated mentions keep their DOM nodes so they can be moved into
 * {@link InteractiveTableView} without losing Solid mounts.
 */
export function parseTableElement(table: HTMLTableElement): {
	headers: ParsedTableCell[];
	rows: ParsedTableCell[][];
} {
	const { header, body } = tableRowElements(table);
	return {
		headers: header ? Array.from(header.cells).map(parseTableCell) : [],
		rows: body.map((row) => Array.from(row.cells).map(parseTableCell)),
	};
}

export function tableCellText(cell: ParsedTableCell | string): string {
	if (typeof cell === "string") {
		return cell;
	}
	return cell.kind === "text" ? cell.value : "";
}

export function isRichTableCell(
	cell: ParsedTableCell | string,
): cell is Extract<ParsedTableCell, { kind: "rich" }> {
	return typeof cell !== "string" && cell.kind === "rich";
}
